/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: dashboard
 * (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../controls/DrillThroughController',
	'../managers/DrillThroughMappingManager',
	'../../util/JumpToActionHelper',
	'underscore'
], function(BaseClass, DrillThroughController, DrillThroughMappingManager, JumpToActionHelper, _) {

	var DrillThroughService = BaseClass.extend({
		/**
		 * The drill through service extension which manages access to the drill through APIs and managers
		 * initialized with its model (as defined in the boardModelExtension).
		 */
		init: function(options) {
			DrillThroughService.inherited('init', this, arguments);
			this.dashboardApi = options.dashboardApi;

			this._drillThroughController = new DrillThroughController(this.dashboardApi, this);
			this._mappingManager = new DrillThroughMappingManager(this.dashboardApi);
		},

		/**
		* Get public api of drill through model object
		*/
		getDrillThroughModelApi: function() {
			// TODO - livewidget_cleanup -- there is no need for this to be asynch
			if (!this.drillThrough) {
				var boardModel = this.dashboardApi.getFeature('internal').getBoardModel();
				this.drillThrough = boardModel.get('drillThrough');
			}
			return Promise.resolve(this.drillThrough.getAPI());
		},

		/**
		* Get public api of drill through model object synchronously
		*/
		getDrillThroughModelApiSync: function() {
			if (this.drillThrough) {
				return this.drillThrough.getAPI();
			}

			this.drillThrough = this.dashboardApi.getFeature('internal')
				.getBoardModel().get('drillThrough');
			return this.drillThrough.getAPI();
		},

		/**
		 * Get drill through definition by widget and widget groups.
		 * @param {object} widgetModel - the widget model to get the drill through definitions
		 */
		getDrillThroughByWidgetAndGroups: function(widgetModel) {
			let boardModel = this.dashboardApi.getFeature('internal').getBoardModel();
			let associatedDrillDefinitions = [];
			let drillThroughDefinitions = boardModel.drillThrough.getModels();
			let eventGroup = boardModel.eventGroups.findGroup(widgetModel.get('id'));
			drillThroughDefinitions.forEach((drillDefinition) => {
				if ((drillDefinition.scope === 'visualization' && drillDefinition.ownerId === widgetModel.get('id'))
					|| (drillDefinition.scope === 'connection' && eventGroup && eventGroup.widgetIds.indexOf(drillDefinition.ownerId) > -1)
					|| (drillDefinition.scope === 'dashboard')) {
					// Clean mappings from current dashboard if it doesn't included in the widgetModel
					if (drillDefinition.mappings && drillDefinition.mappings.length) {
						drillDefinition.mappings = JSON.parse(JSON.stringify(drillDefinition.mappings));
						drillDefinition.mappings.forEach((mapping) => {
							if (mapping.mapTo) {
							// Search in projected data items
								let widgetDataViews = widgetModel.data && widgetModel.data.dataViews;
								let dataItemInWidget;
								_.find(widgetDataViews, dataView => {
									return _.find(dataView && dataView.dataItems, dataItem => {
										const mappedToDataItem = dataItem && dataItem.itemId === mapping.mapTo;
										if (mappedToDataItem) {
											dataItemInWidget = dataItem;
										}
										return mappedToDataItem;
									});
								});

								if (!dataItemInWidget) {
									// pageContext are converted to local filters and copied to the new widget so look at the local filters only
									// Search in filters
									let localFilters = widgetModel.localFilters;
									dataItemInWidget = _.find(localFilters, (localFilter) => {
										return localFilter.id === mapping.mapTo ||  localFilter.columnId === mapping.mapTo;
									});

									if (!dataItemInWidget) {
										delete mapping.mapTo;
									}
								}
							}
						});
					}
					associatedDrillDefinitions.push(drillDefinition);
				}
			});
			return associatedDrillDefinitions;
		},


		/**
		 * Add drill through definitions when add dashboard fragment
		 */
		addDrillThroughOnAddFragment: function(fragmentBoardModel, sourceIdMap, widgetIdMap, selectedLayoutId) {
			let drillThroughEntries = fragmentBoardModel && fragmentBoardModel.drillThrough;
			if (drillThroughEntries && drillThroughEntries.length) {
				let drillThroughModelApi = this.getDrillThroughModelApiSync();
				this._applyTargetMatchingRulesForAddFragment(drillThroughEntries, drillThroughModelApi, selectedLayoutId);
				drillThroughEntries.forEach((drillDefinition) => {
					// Update the modelRef to the new sourceId (currently only one source is supported)
					let drillThroughDataSource = drillDefinition.modelRefs[0];
					if (sourceIdMap && sourceIdMap[drillThroughDataSource]) {
						drillDefinition.modelRefs = [sourceIdMap[drillThroughDataSource]];
					}

					// Preserve the new widget Id as the new drill through definition owner
					// when pasting to a new Dashboard.
					// For the scenarios such as pasting a widget to same tab or different tab within a Dashboard,
					// just do a simple update, i.e. no ownership will be changed.
					let options = {};
					let newWidgetId = widgetIdMap[drillDefinition.ownerId];
					if (newWidgetId) {
						options.newOwnerId = newWidgetId;
					}

					drillThroughModelApi.addUpdateDrillDefinitionEntry(drillDefinition, options);
				});
			}
		},

		/**
		 * Apply additional rules needed when adding a fragment (copying/pinning drillThroughs)
		 * for when to copy and when to merge drill throughs.
		 * For visualization, force a new drill through to be created all the time by setting id to null
		 * For datasource, only copy if no drill through exists
		 * For connection drillthrough - search the target page for existing drill throughs:
		 * 		- add (set id to null) if no matching drill through exists on the target page
		 * 		- merge (use that id) if a matching drillthrough definition exists on the target page
		 * @param {Object} drillThroughFragment - the fragment which holds information about the drillthroughs being pasted.
		 * @param {Object} drillThroughModelApi - the drill through Api
		 * @param {String} selectedLayoutId - the id of the selected layout.
		 */
		_applyTargetMatchingRulesForAddFragment(drillThroughFragment, drillThroughModelApi, selectedLayoutId) {
			const targetEventGroupInfo = this._getTargetEventGroupInfo(selectedLayoutId);
			if (!targetEventGroupInfo) {
				return;
			}
			const drillThroughsInTarget = drillThroughModelApi.getDrillDefinitionEntries();

			drillThroughFragment.forEach((drillThroughToPaste) => {
				if (drillThroughToPaste.scope==='visualization') {
					//Always create a new Id for visualization drillThroughs.  Evey one is unique.
					drillThroughToPaste.id = null;
				} else if (drillThroughToPaste.scope==='connection') {
					const matchingDrillThroughInTargetGroup = this._findMatchingDrillThroughInTargetGroup(
						drillThroughToPaste,
						drillThroughsInTarget,
						targetEventGroupInfo.defaultEventGroupId
					);
					if (matchingDrillThroughInTargetGroup) {
						//update the existing drill through def.
						drillThroughToPaste.id = matchingDrillThroughInTargetGroup.getId();
					} else {
						//No drill through found on the target page/group....create a new id for this page.
						drillThroughToPaste.id = null;
					}
				}
				//Assign the drill through to the default event group for the target page.
				drillThroughToPaste.eventGroupId = targetEventGroupInfo.defaultEventGroupId;
			});
		},

		_getTargetEventGroupInfo(selectedLayoutId) {
			//Get information about the event groups on the target page...
			if (selectedLayoutId) {
				const eventGroupsFeature = this.dashboardApi.getFeature('EventGroups');
				const eventGroupsOnPage = eventGroupsFeature && eventGroupsFeature.getGroupIdsOnPage(selectedLayoutId);
				const currentPage = this.dashboardApi.getCanvas().getContent(selectedLayoutId);
				const defaultEventGroupId = eventGroupsFeature && eventGroupsFeature.getDefaultGroupId(currentPage);
				return {
					eventGroupsOnPage: eventGroupsOnPage,
					defaultEventGroupId: defaultEventGroupId
				};
			}
			return null;
		},

		_findMatchingDrillThroughInTargetGroup(drillThroughToPaste, existingDrillThroughsInTarget, targetEventGroupId) {
			//The target asset should be considered a match if either the id or search path matches.
			const _targetAssetMatches = (existingDrillThrough) => {
				return (
					drillThroughToPaste.assetId === existingDrillThrough.getAssetId() ||
					drillThroughToPaste.assetSearchPath === existingDrillThrough.getAssetSearchPath()
				);
			};

			const matchingDT = existingDrillThroughsInTarget.find((existingDrillThrough) => {
				return (
					//The existing drill through's event group must match the target where we're pasting.
					targetEventGroupId === existingDrillThrough.getEventGroupId() &&

					//Other attributes must match the object being pasted to be considered a match.
					//Don't include the ownerId in the comparison.
					drillThroughToPaste.name === existingDrillThrough.getName() &&
					_targetAssetMatches(existingDrillThrough) &&
					drillThroughToPaste.type === existingDrillThrough.getType() &&
					drillThroughToPaste.perspective === existingDrillThrough.getPerspective() &&
					_.isEqual(drillThroughToPaste.modelRefs, existingDrillThrough.getModelRefs()) &&
					_.isEqual(drillThroughToPaste.mappings, existingDrillThrough.getMappings()) &&
					drillThroughToPaste.scope === existingDrillThrough.getScope()
				);
			});

			return matchingDT;
		},


		getDrillThroughController: function() {
			return this._drillThroughController;
		},

		getDrillThroughMappingManager: function() {
			return this._mappingManager;
		},

		getJumpToTargets: function(options){
			return JumpToActionHelper.getJumpToTargets(options);
		},
		getDeploymentReferences: function() {
			let drillEntries = this.getDrillThroughModelApiSync().getDrillDefinitionEntries();
			const userProfile = this.dashboardApi.getGlassCoreSvc('.UserProfile');
			const userPreferences = userProfile && userProfile.preferences;
			const contentLocale = userPreferences && userPreferences.contentLocale || 'en-us';
			let deploymentReferences = [];
			drillEntries.forEach(function(entry) {
				deploymentReferences.push({
					objects: [{
						type: entry.getType(),
						searchPath: 'storeID("' + entry.getAssetId() + '")'
					}],
					name: {
						[contentLocale]: entry.getAssetId()
					}
				});
			});

			return deploymentReferences;
		}
	});

	return DrillThroughService;
});
